import "./OrgOutcomesView.scss";

import { FC, useEffect } from "react";

import { OUTCOME_ID_QUERY_PARAM } from "components/constants";
import { OutcomeCount } from "components/materials/home/admin/outcomes/OutcomeCount";
import { OutcomeGroupCard } from "components/materials/home/admin/outcomes/OutcomeGroupCard";
import { OutcomeChipList } from "components/materials/outcomes/OutcomeChipList";
import { OutcomeInfo } from "components/outcomes/OutcomeInfo";
import { OutcomeType } from "components/server-types";
import { t } from "i18n/i18n";
import { TextAddButton } from "mds/components/TextAddButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { storeApi, useAppSelector } from "store/index";
import {
  selectIsEditingOrgSettings,
  selectNestedOutcomeGroups,
  selectOrgOutcomeRubric,
} from "store/selectors";
import { useFindItemBySearchParamId } from "utils/hooks/use-find-item-by-search-param-id";
import { useNavigate } from "utils/routing";
import { homeOutcomesUrl, withQueryParams } from "utils/urls";

export const AdminOutcomesView: FC = () => {
  const navigate = useNavigate();
  const outcomeGroups = useAppSelector(selectNestedOutcomeGroups);
  const canEdit = useAppSelector(selectIsEditingOrgSettings);

  const totalOutcomeCount = outcomeGroups.reduce((acc, group) => acc + group.outcomeCount, 0);

  const outcomes = outcomeGroups
    .map((outcomeGroup) => outcomeGroup.outcomes)
    .flat()
    .concat(
      outcomeGroups
        .map((outcomeGroup) => outcomeGroup.outcomeSubgroups)
        .flat()
        .map((subgroup) => subgroup.outcomes)
        .flat(),
    );

  const currentOutcome = useFindItemBySearchParamId(outcomes, OUTCOME_ID_QUERY_PARAM);

  const rubric = useAppSelector((s) => selectOrgOutcomeRubric(s, currentOutcome?.id));

  const onOutcomeChange = (outcome: OutcomeType) => {
    navigate(withQueryParams("", { [OUTCOME_ID_QUERY_PARAM]: outcome.id }));
  };

  useEffect(() => {
    storeApi.outcomes.list();

    storeApi.outcome_subgroups.list();

    storeApi.outcome_groups.list();
  }, []);

  const createFirstOutcome = () => {
    // If there are no outcome groups, create the default outcome group "Outcomes", and then create the first outcome.
    // TODO: If we need to create the default outcome group for user, we likely missed a set-up step and should fix it.
    // Note that the outcome's initial blank title will be replaced by the server with an auto-generated title.
    // The blank title is required in the context of unit tests, where server interactions are stubbed out.
    if (outcomeGroups.length === 0) {
      storeApi.outcome_groups
        .create({
          title: t("glossary.outcomes"),
        })
        .then((newOutcomeGroup) => {
          storeApi.outcomes
            .create({ outcome_group_id: newOutcomeGroup.id, title: "" })
            .then(onOutcomeChange);
        });
    } else {
      // Create the first outcome in the first outcome group.
      storeApi.outcomes
        .create({ outcome_group_id: outcomeGroups[0].id, title: "" })
        .then(onOutcomeChange);
    }
  };

  const isSmallScreen = !useIsMdOrLarger();

  if (isSmallScreen) {
    return (
      <div className="h-full overflow-y-auto pb-4">
        <div className="flex flex-col gap-4">
          <OutcomeChipList
            getOutcomeUrl={(outcome) => homeOutcomesUrl("admin", outcome.id)}
            outcomes={outcomes}
            selectedOutcomeId={currentOutcome?.id}
            size="m"
          />
          <OutcomeInfo
            isCourseLevel={false}
            outcomeItem={currentOutcome}
            rubric={rubric}
            readOnly
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full">
      <div className="org-outcomes-sidebar h-full w-full flex-auto flex-col gap-4 overflow-y-auto bg-black-tint-99 px-3 py-4">
        <div className="flex justify-between">
          <h3 className="h3 my-0 px-2 py-1 text-black-tint-40">{t("outcomes.total_outcomes")}</h3>
          <div className="flex">
            {/* We are disabling the deprecated outcomes button until support for this is implemented. */}
            {/* <LegacyButton */}
            {/*  className="items-center flex gap-1" */}
            {/*  kind="tertiary" */}
            {/*  size="xs" */}
            {/*  onClick={() => showComingSoonAlert()} */}
            {/* > */}
            {/*  <EyeVisibleIcon /> */}
            {/*  {t("outcomes.show_deprecated")} */}
            {/*  <ChevronDownIcon /> */}
            {/* </LegacyButton> */}
            <OutcomeCount count={totalOutcomeCount} />
          </div>
        </div>

        {outcomeGroups.map((group) => (
          <OutcomeGroupCard
            currentOutcome={currentOutcome}
            key={group.id}
            outcomeGroup={group}
            queryParam={OUTCOME_ID_QUERY_PARAM}
          />
        ))}

        {canEdit && (
          <TextAddButton
            className="add-group"
            eventName="Org Outcomes - Add group"
            reference="outcome_group"
            onClick={() => {
              storeApi.outcome_groups.create({
                title: t("common.untitled.outcome_group"),
              });
            }}
          />
        )}
      </div>

      <main className="h-full flex-auto">
        <OutcomeInfo
          createNewOutcome={outcomes.length === 0 ? createFirstOutcome : undefined}
          isCourseLevel={false}
          outcomeItem={currentOutcome}
          readOnly={!canEdit}
          rubric={rubric}
        />
      </main>
    </div>
  );
};
